/**
 * Hook mode — right-side touch joystick for laser aim (mobile only).
 */

import { pointHookAimFromStick } from "./hook-mode";

const DEAD_ZONE = 0.14;
const MAX_R = 52;

let mounted = false;
let root: HTMLElement | null = null;
let knob: HTMLElement | null = null;
let active = false;
let pointerId = -1;
let centerX = 0;
let centerY = 0;

function syncKnob(dx: number, dy: number) {
    if (!knob) return;
    const m = Math.hypot(dx, dy);
    if (m > MAX_R && m > 1e-6) {
        const s = MAX_R / m;
        dx *= s;
        dy *= s;
    }
    knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
}

function resetKnob() {
    if (knob) knob.style.transform = "translate(-50%, -50%)";
}

export function mountHookAimJoystick() {
    if (mounted) return;
    mounted = true;
    root = document.getElementById("hookAimJoystick");
    knob = root?.querySelector<HTMLElement>(".hook-aim-joystick-knob") ?? null;
    if (!root) return;

    root.addEventListener(
        "pointerdown",
        (e) => {
            if (root?.classList.contains("hidden")) return;
            if (e.pointerType !== "touch" && e.pointerType !== "pen") return;
            active = true;
            pointerId = e.pointerId;
            const rect = root.getBoundingClientRect();
            centerX = rect.left + rect.width * 0.5;
            centerY = rect.top + rect.height * 0.5;
            try {
                root.setPointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
            e.preventDefault();
            e.stopPropagation();
            updateFromClient(e.clientX, e.clientY);
        },
        { passive: false },
    );

    root.addEventListener(
        "pointermove",
        (e) => {
            if (!active || e.pointerId !== pointerId) return;
            e.preventDefault();
            updateFromClient(e.clientX, e.clientY);
        },
        { passive: false },
    );

    const end = (e: PointerEvent) => {
        if (!active || e.pointerId !== pointerId) return;
        active = false;
        pointerId = -1;
        resetKnob();
        try {
            root?.releasePointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
    };
    root.addEventListener("pointerup", end);
    root.addEventListener("pointercancel", end);
}

function updateFromClient(clientX: number, clientY: number) {
    const dx = clientX - centerX;
    const dy = clientY - centerY;
    syncKnob(dx, dy);
    const m = Math.hypot(dx, dy);
    if (m < 6) return;
    pointHookAimFromStick(dx / MAX_R, dy / MAX_R, DEAD_ZONE);
}

export function setHookAimJoystickVisible(on: boolean) {
    if (!root) root = document.getElementById("hookAimJoystick");
    if (!root) return;
    root.classList.toggle("hidden", !on);
    if (!on) {
        active = false;
        pointerId = -1;
        resetKnob();
    }
}
